import * as React from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { Popover, PopoverContent, PopoverTrigger } from './popover.js';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from './command.js';
import { InfoHint } from './info-hint.js';
import {
  PILL_BUTTON_BASE,
  PILL_BUTTON_IDLE,
  PILL_BUTTON_ACTIVE,
  PILL_VALUE_CLASS,
  PILL_VALUE_IDLE_CLASS,
  PillWrapper,
} from './filter-pill-primitives.js';
import type { GroupedSelectFilterField, FilterPanelLabels } from './filter-pill-primitives.js';
import { namespacedKey, parseNamespacedKey } from '../lib/grouped-filter.js';

// ─── GroupedFilterPill ──────────────────────────────────────────
//
// Multi-select popover split into labeled option groups (e.g. one group per
// admin-division level) with a SINGLE search box that filters across every
// group at once via cmdk's default fuzzy matching. All selected values live
// in one flat array (same contract as every other FilterPanel field) — use
// `bucketGroupedFilterValues` (lib/grouped-filter.ts) to split them back into
// per-group buckets when the consuming app needs to know which group a value
// came from.
//
// Each flat-array entry is NAMESPACED via `namespacedKey` (lib/grouped-filter.ts)
// so an identical raw value selected from two different groups (e.g. county
// "Kraków" and city "Kraków") never collides in the flat selection model. The
// namespace is internal to this component's selection tracking —
// `bucketGroupedFilterValues` strips it back off before the raw value reaches
// API filters. `namespacedKey`/`parseNamespacedKey` are shared from
// lib/grouped-filter.ts so every consumer agrees on the separator.
//
// Mirrors DateRangePill's prop shape (field/selected/onChange/onClear/labels/
// autoOpen/onAutoOpened) so it plugs into FilterPanel the same way.

function renderGroupHeading(label: string, isFirst: boolean) {
  return (
    <span
      className={cn(
        'block px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground',
        isFirst ? 'pt-1' : 'mt-1 border-t border-border/40 pt-2',
      )}
    >
      {label}
    </span>
  );
}

export interface GroupedFilterPillProps {
  field: GroupedSelectFilterField;
  /** All selected option values across every group, as one flat array. */
  selected: string[];
  onChange: (id: string, values: string[]) => void;
  onClear: (id: string) => void;
  isLoading?: boolean;
  labels: FilterPanelLabels;
  autoOpen?: boolean;
  onAutoOpened?: () => void;
}

export function GroupedFilterPill({
  field,
  selected,
  onChange,
  onClear,
  isLoading,
  labels,
  autoOpen,
  onAutoOpened,
}: GroupedFilterPillProps) {
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isActive = selected.length > 0;

  React.useEffect(() => {
    if (autoOpen && !open) {
      setOpen(true);
      onAutoOpened?.();
    }
  }, [autoOpen, open]);

  React.useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  /** De-namespaces a flat selection key and resolves its option label. */
  function labelFor(namespacedValue: string): string {
    const parsed = parseNamespacedKey(namespacedValue);
    if (!parsed) return namespacedValue;
    const group = field.groups.find((g) => g.id === parsed.groupId);
    const opt = group?.options.find((o) => o.value === parsed.value);
    return opt ? opt.label : parsed.value;
  }

  function toggleOption(groupId: string, value: string) {
    const key = namespacedKey(groupId, value);
    const next = selected.includes(key) ? selected.filter((v) => v !== key) : [...selected, key];
    onChange(field.id, next);
  }

  function handleClear(e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation();
    onClear(field.id);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PillWrapper label={field.label} showLabel={isActive}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            data-testid={`filter-trigger-${field.id}`}
            disabled={isLoading}
            className={cn(PILL_BUTTON_BASE, isActive ? PILL_BUTTON_ACTIVE : PILL_BUTTON_IDLE)}
          >
            {isActive ? (
              <>
                <span className="flex flex-wrap items-center gap-1 text-[11px] text-foreground">
                  {selected.map((value) => (
                    <span key={value} className={PILL_VALUE_CLASS}>
                      {labelFor(value)}
                    </span>
                  ))}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={labels.getClearAriaLabel(field.id)}
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleClear(e);
                  }}
                  className="shrink-0 hover:opacity-70 rounded-full p-0.5"
                >
                  <X className="h-3 w-3 text-foreground/60" />
                </span>
              </>
            ) : (
              <>
                <span className={cn(PILL_VALUE_IDLE_CLASS, 'text-muted-foreground')}>
                  {field.label}
                </span>
                <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
              </>
            )}
          </button>
        </PopoverTrigger>
      </PillWrapper>
      <PopoverContent
        className={cn('p-0', field.popoverClassName ?? 'w-64')}
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {field.infoHint ? (
          <div className="flex items-center gap-1 px-3 pt-2 pb-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {field.label}
            </span>
            <InfoHint label={field.infoHintLabel ?? field.label} side="right" iconSize={12}>
              {field.infoHint}
            </InfoHint>
          </div>
        ) : null}
        <Command>
          <CommandInput
            ref={inputRef}
            placeholder={field.searchPlaceholder ?? labels.searchPlaceholder}
          />
          <CommandList className="max-h-72">
            <CommandEmpty>{/* No results */}</CommandEmpty>
            {field.groups.map((group, index) => (
              <CommandGroup key={group.id} heading={renderGroupHeading(group.heading, index === 0)}>
                {group.options.map((opt) => {
                  const checked = selected.includes(namespacedKey(group.id, opt.value));
                  return (
                    <CommandItem
                      key={opt.value}
                      value={opt.label}
                      onSelect={() => {
                        toggleOption(group.id, opt.value);
                        inputRef.current?.focus();
                      }}
                    >
                      <Check
                        className={cn(
                          'h-3.5 w-3.5 shrink-0',
                          checked ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      {opt.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
